import React from 'react'
import { styled, useTheme } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import { useQuery } from 'react-query'
import { alpha, Button, Stack, Typography, useMediaQuery } from '@mui/material'
import { selectOptionsFromTableResponse } from 'utils'
import { familyAddNoteTooltip, searchTooltip, traineeAddNoteTooltip } from '../tooltips/tooltips'
import { useShowTotalEngagement } from '../../features/Metric/hooks/useShowTotalEngagement'
import { useDrawersContext } from '../context/ClientsDrawersProvider'
import { useAuthContext } from '../context/AuthProvider'
import NotificationSys from '../NotificationSystem'
import HelpTooltip from '../tooltips/HelpTooltip'
import { getTags } from 'api/tags'
import { useSearchInput } from './useSearchInput'
import { FilterModel } from 'types'

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.black, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.black, 0.25),
  },
  marginRight: theme.spacing(1),
  width: 'auto',
}))

const getTagFilterById = (id: number): FilterModel => ({
  tagId: {
    type: 'number',
    op: 'equals',
    value: id,
  },
})

const FooterContent = () => {
  const theme = useTheme()
  const { isTrainee } = useAuthContext()
  const { handleOpenNoteEdit, handleOpenSearchDrawer } = useDrawersContext()
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'))

  const { totalTimeText } = useShowTotalEngagement()

  const tagsQuery = useQuery(['getTags'], () => getTags(), {
    select: selectOptionsFromTableResponse,
  })

  const { SearchInput, searchInput } = useSearchInput({
    options: tagsQuery.data,
    isLoading: tagsQuery.isLoading,
    getFilters: getTagFilterById,
    onSearch: handleOpenSearchDrawer,
  })

  const onSearch = () => {
    if (searchInput.trim().length < 3) {
      NotificationSys.showWarning('Please enter 3 symbols or more to be able to search.')
      return
    }

    handleOpenSearchDrawer(searchInput, null)
  }

  const onClickAddNote = () => {
    handleOpenNoteEdit()
  }

  return (
    <Stack
      spacing={1}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flexGrow={1}
    >
      {!matchesXs && (
        <Stack spacing={2} direction="row" alignItems="center">
          <Stack direction="row">
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>

              {SearchInput}
            </Search>

            <HelpTooltip text={searchTooltip} placement="top">
              <Button variant="contained" onClick={onSearch}>
                Search
              </Button>
            </HelpTooltip>
            <HelpTooltip
              text={isTrainee ? traineeAddNoteTooltip : familyAddNoteTooltip}
              placement="top"
            >
              <Button variant="contained" color="secondary" onClick={onClickAddNote} sx={{ ml: 4 }}>
                Add note
              </Button>
            </HelpTooltip>
          </Stack>
        </Stack>
      )}

      {totalTimeText && (
        <Stack direction="row" justifyContent="flex-end" flexGrow={1}>
          <Typography variant="body2" color="textSecondary">
            Total Time Online:{'\u00A0'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {totalTimeText}
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}

export default React.memo(FooterContent)
